import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client');
import { z } from 'zod';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const { createContext, useContext, useEffect, useState } = React;

// Zod schema for theme validation
export const ThemeSchema = z.enum(['light', 'dark', 'system']);

// Theme types derived from schema
export type Theme = z.infer<typeof ThemeSchema>;

// Resolved theme (what actually gets applied)
export type ResolvedTheme = 'light' | 'dark';

// Zod schema for theme configuration
export const ThemeConfigSchema = z.object({
  theme: ThemeSchema,
  defaultTheme: ThemeSchema.optional(),
  storageKey: z.string().optional(),
});

// Zod schema for persisted theme state
export const ThemeStateSchema = z.object({
  state: z.object({
    theme: ThemeSchema,
  }),
  version: z.number().optional(),
});

export type ThemeState = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

// Helper function to get system theme
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Helper function to resolve theme
const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme as ResolvedTheme;
};

// Zustand store for theme state management
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolvedTheme: getSystemTheme(),
      setTheme: (theme: Theme) => {
        // Validate theme using Zod schema
        const validatedTheme = ThemeSchema.parse(theme);
        const resolved = resolveTheme(validatedTheme);

        set({ theme: validatedTheme, resolvedTheme: resolved });

        // Apply resolved theme to document immediately for <100ms performance
        document.documentElement.setAttribute('data-theme', resolved);

        // Apply theme class for Tailwind dark mode
        if (resolved === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        // Sync back to system settings store if available
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            // Import system settings store dynamically to avoid circular dependencies
            import('../stores/system-settings-store')
              .then(({ useSystemSettingsStore }) => {
                const systemStore = useSystemSettingsStore.getState();
                if (systemStore.preferences.theme !== validatedTheme) {
                  systemStore.updateTheme(validatedTheme);
                }
              })
              .catch(error => {
                logger.warn('Could not sync theme with system settings store:', error);
              });
          } catch (error) {
            logger.warn('Could not sync theme with system settings store:', error);
          }
        }
      },
      toggleTheme: () => {
        const currentResolved = get().resolvedTheme;
        const newTheme = currentResolved === 'light' ? 'dark' : 'light';
        get().setTheme(newTheme);
      },
    }),
    {
      name: 'theme-storage',
      // Persist theme selection for workflow continuity
      storage: createJSONStorage(() => window.localStorage),
      version: 1,
      // Migration function to handle schema changes
      migrate: (persistedState: any, version: number) => {
        // If no version or version 0, reset to initial state
        if (!version || version < 1) {
          logger.info(
            'Migrating theme store from version',
            version,
            'to 1 - resetting to system theme'
          );
          return {
            theme: 'system',
            resolvedTheme: getSystemTheme(),
          };
        }

        // For future migrations, handle version upgrades here
        return persistedState;
      },
    }
  )
);

// Context for theme utilities
interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  isLoaded: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme Provider Component
export type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: ThemeProviderProps): React.ReactElement {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useThemeStore();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Initialize theme on mount
    const storedTheme = window.localStorage.getItem('theme-storage');
    const storedSystemSettings = window.localStorage.getItem('system-settings-store');

    // Check if system settings store has a theme preference
    let systemTheme: Theme | null = null;
    if (storedSystemSettings) {
      try {
        const systemData = JSON.parse(storedSystemSettings);
        if (systemData?.state?.preferences?.theme) {
          systemTheme = systemData.state.preferences.theme;
        }
      } catch (error) {
        logger.warn('Could not parse system settings theme:', error);
      }
    }

    // Priority: stored theme-storage > system-settings-store > defaultTheme
    if (storedTheme) {
      try {
        const parsedData = JSON.parse(storedTheme) as unknown;
        // Validate and parse stored theme data using Zod
        const validatedData = ThemeStateSchema.parse(parsedData);
        const themeValue = validatedData.state.theme;
        setTheme(themeValue);
      } catch (error) {
        // If parsing fails, try system theme or fall back to default theme
        logger.warn('Invalid stored theme data, trying system theme or default:', error);
        const fallbackTheme = systemTheme || defaultTheme;
        setTheme(ThemeSchema.parse(fallbackTheme));
      }
    } else if (systemTheme) {
      // Use theme from system settings store
      setTheme(ThemeSchema.parse(systemTheme));
    } else {
      // Use default theme
      setTheme(ThemeSchema.parse(defaultTheme));
    }

    setIsLoaded(true);
  }, [defaultTheme, setTheme]);

  useEffect(() => {
    // Listen for system theme changes when theme is set to 'system'
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      const newResolvedTheme = e.matches ? 'dark' : 'light';

      // Update the resolved theme in the store
      const currentState = useThemeStore.getState();
      if (currentState.theme === 'system') {
        useThemeStore.setState({
          ...currentState,
          resolvedTheme: newResolvedTheme,
        });

        // Apply the new resolved theme
        document.documentElement.setAttribute('data-theme', newResolvedTheme);
        if (newResolvedTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };

    // Add listener for system theme changes
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]);

  useEffect(() => {
    // Apply resolved theme to document root for CSS custom properties
    document.documentElement.setAttribute('data-theme', resolvedTheme);

    // Apply theme class for Tailwind dark mode
    if (resolvedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [resolvedTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme,
        isLoaded,
      }}
    >
      <div
        data-testid="theme-root"
        className="flex flex-1 transition-colors duration-100 w-screen h-screen flex-col items-center justify-center"
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

// Hook to use theme
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

// Theme toggle button component
export type ThemeToggleProps = {
  className?: string;
  'data-testid'?: string;
};

export function ThemeToggle({
  className = '',
  'data-testid': testId = 'theme-toggle',
}: ThemeToggleProps): React.ReactElement {
  const { theme, resolvedTheme, toggleTheme, isLoaded } = useTheme();

  if (!isLoaded) {
    return <div className={`w-10 h-6 ${className}`} />; // Placeholder to prevent layout shift
  }

  const getThemeLabel = () => {
    if (theme === 'system') {
      return `System (${resolvedTheme})`;
    }
    return theme;
  };

  const getNextThemeLabel = () => {
    if (theme === 'system') {
      return resolvedTheme === 'light' ? 'dark' : 'light';
    }
    return theme === 'light' ? 'dark' : 'light';
  };

  return (
    <button
      data-testid={testId}
      onClick={toggleTheme}
      className={`
 inline-flex items-center justify-center
 w-10 h-6 rounded-full
 border border-border
 bg-background
 hover:bg-muted
 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
 transition-all duration-100
 ${className}
 `}
      aria-label={`Switch to ${getNextThemeLabel()} theme`}
      title={`Current theme: ${getThemeLabel()}. Click to switch to ${getNextThemeLabel()} theme.`}
    >
      <div
        className={`
 w-4 h-4 rounded-full
 bg-foreground
 transition-transform duration-100
 ${resolvedTheme === 'dark' ? 'translate-x-2' : '-translate-x-2'}
 `}
      />
    </button>
  );
}
